// DRV-5a (#2883) — the account-change confirmation after a Reconnect bounced
// `needs_confirmation` (DRV-1 Q2: the callback changed nothing; the switch
// waits in metadata.pendingAccount until an admin confirms or it expires).
//
// Admin Settings path only (AuthorizeGDrivePage mode='settings'). It reads
// both accounts from GET /tenant-integrations/me — admin-rank metadata.
// Confirm → POST /tenant-integrations/gdrive/confirm-account-change (admin
// rank, server-enforced) → { authUrl }, handed to the page to open the popup.
// Cancel makes NO request: the existing connection is untouched, and the
// pending switch simply expires.

import { useEffect, useState } from 'react';
import { apiFetch } from '../../lib/api';
import { fetchDriveIntegration, formatDriveDate, type DriveIntegration } from '../../lib/driveIntegration';

interface Props {
  onConfirmed: (authUrl: string) => void;
  onCancel: () => void;
}

type Load = { kind: 'loading' } | { kind: 'error' } | { kind: 'ready'; integration: DriveIntegration; loadedAt: number };

export const CONFIRM_ACCOUNT_CHANGE_PATH = '/tenant-integrations/gdrive/confirm-account-change';

export default function DriveAccountSwitchDialog({ onConfirmed, onCancel }: Props) {
  const [load, setLoad] = useState<Load>({ kind: 'loading' });
  const [confirming, setConfirming] = useState(false);
  const [confirmFailed, setConfirmFailed] = useState(false);

  useEffect(() => {
    let active = true;
    fetchDriveIntegration()
      .then((integration) => { if (active) setLoad({ kind: 'ready', integration, loadedAt: Date.now() }); })
      .catch(() => { if (active) setLoad({ kind: 'error' }); });
    return () => { active = false; };
  }, []);

  function confirm() {
    setConfirming(true);
    setConfirmFailed(false);
    apiFetch<{ authUrl: string }>(CONFIRM_ACCOUNT_CHANGE_PATH, { method: 'POST' })
      .then((res) => onConfirmed(res.authUrl))
      .catch(() => setConfirmFailed(true))
      .finally(() => setConfirming(false));
  }

  const pending = load.kind === 'ready' ? load.integration.metadata?.pendingAccount ?? null : null;
  const current = load.kind === 'ready' ? load.integration.metadata?.account ?? null : null;
  const expired = !!pending && load.kind === 'ready' && !(Date.parse(pending.expiresAt) > load.loadedAt);

  return (
    <div data-testid="drive-switch-dialog" role="dialog" aria-labelledby="drive-switch-title">
      <h2 id="drive-switch-title" style={titleStyle}>Switch Google Drive account?</h2>

      {load.kind === 'loading' && <p style={textStyle}>Loading…</p>}

      {(load.kind === 'error' || (load.kind === 'ready' && (!pending || expired))) && (
        <p data-testid="drive-switch-none" style={textStyle}>
          There is no account switch waiting for confirmation (it may have expired). Drive stays
          connected to its current account. Reconnect to start again.
        </p>
      )}

      {pending && !expired && (
        <>
          <p style={textStyle}>
            You signed in with a different Google account. Nothing has changed yet: Drive is still
            connected to the original account.
          </p>
          <p style={textStyle}>
            Switch from{' '}
            <strong data-testid="drive-switch-current">{current?.emailAddress ?? 'the current account'}</strong>
            {' '}to{' '}
            <strong data-testid="drive-switch-pending">{pending.emailAddress ?? 'the new account'}</strong>?{' '}
            <span data-testid="drive-switch-expires">(expires {formatDriveDate(pending.expiresAt)})</span>
          </p>
          <p style={textStyle}>
            Confirming opens Google again; sign in as the new account to finish the switch.
          </p>
          {confirmFailed && (
            <p data-testid="drive-switch-error" role="alert" style={errorStyle}>
              Couldn&rsquo;t confirm the switch. It may have expired; reconnect to start again.
            </p>
          )}
        </>
      )}

      <div style={{ display: 'flex', gap: 8 }}>
        {pending && !expired && (
          <button data-testid="drive-switch-confirm" style={primaryBtnStyle} onClick={confirm} disabled={confirming}>
            {confirming ? 'Opening…' : 'Confirm switch'}
          </button>
        )}
        <button data-testid="drive-switch-cancel" style={secondaryBtnStyle} onClick={onCancel} disabled={confirming}>
          {pending && !expired ? 'Cancel' : 'Back'}
        </button>
      </div>
    </div>
  );
}

const titleStyle: React.CSSProperties = {
  fontFamily: 'var(--font-display)', fontSize: 18, fontWeight: 700, color: 'var(--text)', margin: '0 0 12px',
};
const textStyle: React.CSSProperties = {
  fontFamily: 'var(--font-body)', fontSize: 14, color: 'var(--text-secondary)', lineHeight: 1.5, margin: '0 0 12px',
};
const errorStyle: React.CSSProperties = {
  fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--error, #ef4444)', margin: '0 0 12px',
};
const primaryBtnStyle: React.CSSProperties = {
  fontFamily: 'var(--font-body)', fontSize: 15, fontWeight: 600, padding: '10px 24px',
  background: 'var(--primary, #7c3aed)', color: '#fff', border: 'none',
  borderRadius: 'var(--radius-sm, 8px)', cursor: 'pointer',
};
const secondaryBtnStyle: React.CSSProperties = {
  fontFamily: 'var(--font-body)', fontSize: 15, fontWeight: 500, padding: '10px 24px',
  background: 'transparent', color: 'var(--text-secondary)', border: '1px solid var(--border)',
  borderRadius: 'var(--radius-sm, 8px)', cursor: 'pointer',
};
